import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import ProfileHeader from '../../candidate-profile/components/ProfileHeader';
import ProfileHighlights from '../../candidate-profile/components/ProfileHighlights';
import AlertsCard from '../../candidate-profile/components/AlertsCard';
import AICallSummaryCard from '../../candidate-profile/components/AICallSummaryCard';
import ConfirmDialog from '../../../components/ui/ConfirmDialog';
import { useAuth } from '../../../contexts/AuthContext';
import { useCandidate } from '../../../hooks';
import {
  getCandidateAlerts,
  getDemoCandidateCalls,
  getPrioritizedCandidatesCount,
  uploadCandidatesToZohoList,
} from '../../../lib/api';
import { supabase } from '../../../lib/supabase';
import { buildCandidateProfileView } from '../../../lib/utils/candidateProfileView';
import { parseBoolean } from '../../../lib/utils/boolean';
import ZohoUploadResult from '../../../components/ZohoUploadResult';

const MAX_PRIORITIZED = 5;

const snapshotDemoStatus = (candidate) => ({
  is_open_to_work: String(candidate?.is_open_to_work ?? '').trim().toLowerCase(),
  reasoning: String(candidate?.reasoning_open_to_work || '').trim(),
  invalid_link: parseBoolean(candidate?.invalid_link),
});

const hasDemoStatusChanged = (candidate, snapshot) => {
  if (!candidate || !snapshot) return false;
  const next = snapshotDemoStatus(candidate);
  return (
    next.is_open_to_work !== snapshot.is_open_to_work
    || next.reasoning !== snapshot.reasoning
    || next.invalid_link !== snapshot.invalid_link
  );
};

const DemoCandidateProfile = ({
  candidateId,
  onPhoneChange,
  onUnknownChange,
  onDeleteUnknown,
  callsRefreshKey = 0,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const [calls, setCalls] = useState([]);
  const [loadingAlerts, setLoadingAlerts] = useState(false);
  const [showLimitDialog, setShowLimitDialog] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [uploadNotice, setUploadNotice] = useState(null);
  const [statusSnapshot, setStatusSnapshot] = useState(null);

  const { candidate: dbCandidate, loading, error, updateCandidate } = useCandidate(candidateId, {
    realtime: true,
  });

  useEffect(() => {
    setStatusSnapshot(null);
  }, [candidateId]);

  useEffect(() => {
    if (!dbCandidate || statusSnapshot) return;
    setStatusSnapshot(snapshotDemoStatus(dbCandidate));
  }, [dbCandidate, statusSnapshot]);

  useEffect(() => {
    const phone = String(dbCandidate?.phone || '').trim();
    onPhoneChange?.(Boolean(phone && phone !== '+'));
  }, [dbCandidate?.phone, onPhoneChange]);

  useEffect(() => {
    onUnknownChange?.(parseBoolean(dbCandidate?.invalid_link));
  }, [dbCandidate?.invalid_link, onUnknownChange]);

  useEffect(() => {
    const fetchAlerts = async () => {
      if (!candidateId) return;

      try {
        setLoadingAlerts(true);
        const alertsData = await getCandidateAlerts(candidateId);
        setAlerts(alertsData || []);
      } catch (err) {
        console.error('Error fetching candidate alerts:', err);
      } finally {
        setLoadingAlerts(false);
      }
    };

    fetchAlerts();
  }, [candidateId]);

  useEffect(() => {
    if (!candidateId) {
      setCalls([]);
      return undefined;
    }

    let cancelled = false;

    const loadDemoCalls = async () => {
      try {
        const callsData = await getDemoCandidateCalls(candidateId);
        if (!cancelled) setCalls(callsData);
      } catch (callErr) {
        console.error('Error fetching demo candidate calls:', callErr);
      }
    };

    loadDemoCalls();

    const channel = supabase
      .channel(`demo-calls-${candidateId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'candidates_demo_calls',
          filter: `candidate_id=eq.${candidateId}`,
        },
        () => {
          loadDemoCalls();
        },
      )
      .subscribe();

    return () => {
      cancelled = true;
      supabase.removeChannel(channel);
    };
  }, [candidateId, callsRefreshKey]);

  const candidate = useMemo(
    () => buildCandidateProfileView(dbCandidate, alerts, calls),
    [dbCandidate, alerts, calls],
  );

  const handleSyncToATS = async () => {
    if (!user?.id || syncing) return;

    const zohoId = String(dbCandidate?.zoho_id ?? '').trim();
    if (!zohoId) return;

    setUploadNotice(null);
    setUploadResult(null);
    setSyncing(true);
    try {
      const result = await uploadCandidatesToZohoList(user.id, [zohoId]);
      setUploadResult(result);
    } catch (err) {
      console.error('Error syncing to ATS:', err);
      setUploadNotice({
        type: 'error',
        message: err?.message || t('candidateProfile.syncFailed'),
      });
    } finally {
      setSyncing(false);
    }
  };

  const handleUpdateContact = async ({ email, phone }) => {
    if (!candidateId) return;

    await updateCandidate({
      email: email || null,
      phone: phone || null,
    });
  };

  const handleTogglePriority = async () => {
    if (!candidateId || !candidate) return;

    const newPriorityValue = !candidate.isPrioritized;

    if (newPriorityValue && user?.id) {
      try {
        const prioritizedCount = await getPrioritizedCandidatesCount(user.id);
        if (prioritizedCount >= MAX_PRIORITIZED) {
          setShowLimitDialog(true);
          return;
        }
      } catch (err) {
        console.warn('getPrioritizedCandidatesCount failed:', err);
      }
    }

    try {
      await updateCandidate({ prioritize: newPriorityValue }, { updateTimestamp: false });
    } catch (err) {
      console.error('Failed to update priority:', err);
    }
  };

  const handleSetOpenToWorkManual = async (isOpen) => {
    if (!candidateId) return;
    await updateCandidate({
      is_open_to_work: isOpen ? 'true' : 'false',
      is_open_to_work_manual: true,
      is_open_to_work_manual_at: new Date().toISOString(),
    });
  };

  const handleReturnAiWatch = async () => {
    if (!candidateId) return;
    await updateCandidate({
      is_open_to_work_manual: false,
      is_open_to_work_manual_at: null,
    });
  };

  if (!candidateId) {
    return (
      <div className="rounded-lg border border-border bg-card px-4 py-8 text-center text-sm text-muted-foreground">
        {t('demo.profileMissing')}
      </div>
    );
  }

  if (loading || loadingAlerts) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-muted-foreground">{t('candidateProfile.loading')}</p>
        </div>
      </div>
    );
  }

  if (error || !candidate) {
    return (
      <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-8 text-center">
        <h2 className="text-lg font-semibold text-foreground mb-2">
          {t('candidateProfile.failedToLoad')}
        </h2>
        <p className="text-sm text-muted-foreground">
          {error || t('candidateProfile.candidateNotFound')}
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="max-w-7xl mx-auto space-y-4 md:space-y-6 lg:space-y-8 mb-4">
        <div>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground mb-2">
            {t('candidateProfile.title')}
          </h2>
          <p className="text-sm md:text-base text-muted-foreground">
            {t('candidateProfile.subtitle')}
          </p>
        </div>

        <ProfileHeader
          candidate={candidate}
          showDelete={candidate.isInvalidLink}
          hideCareerStatus={candidate.isInvalidLink}
          unavailableInfo={t('demo.unavailableInfo')}
          showDemoStatusNotice={!candidate.isInvalidLink && !hasDemoStatusChanged(dbCandidate, statusSnapshot)}
          showAiAssessmentWhenPresent
          onDeleteCandidate={onDeleteUnknown}
          onUpdateContact={handleUpdateContact}
          onSyncToATS={handleSyncToATS}
          syncing={syncing}
          onTogglePriority={handleTogglePriority}
          onSetOpenToWorkManual={handleSetOpenToWorkManual}
          onReturnAiWatch={handleReturnAiWatch}
          isPrioritized={candidate.isPrioritized}
          hasPhone={Boolean(candidate.phone?.trim())}
        />

        {uploadNotice && (
          <div className={`rounded-lg border px-4 py-3 text-sm ${
            uploadNotice.type === 'success'
              ? 'border-success/30 bg-success/10 text-success'
              : 'border-destructive/30 bg-destructive/10 text-destructive'
          }`}>
            {uploadNotice.message}
          </div>
        )}

        {uploadResult && !syncing && (
          <ZohoUploadResult result={uploadResult} />
        )}

        <ProfileHighlights candidate={candidate} />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 lg:gap-8">
          <AlertsCard alerts={alerts} />
          <AICallSummaryCard candidate={candidate} hideViewAll />
        </div>
      </div>

      {createPortal(
        <ConfirmDialog
          isOpen={showLimitDialog}
          onClose={() => setShowLimitDialog(false)}
          onConfirm={() => setShowLimitDialog(false)}
          title={t('candidates.prioritizationLimitTitle')}
          message={t('candidates.prioritizationLimitMessage', { count: MAX_PRIORITIZED })}
          confirmText={t('common.ok')}
          variant="default"
          hideCancel
        />,
        document.body,
      )}
    </>
  );
};

export default DemoCandidateProfile;
